'use client';

import { cn, NAV_GLYPH, NAV_ROW, navRowState } from '@routly/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { entryFor, NAVIGATION } from './navItems';
import { SidebarFooter } from './SidebarFooter';

/**
 * The rail.
 *
 * Drawn from `@routly/ui`'s row — the same height, corner, glyph scale and
 * accent marker the product's rail uses. It had its own: a taller row with a
 * smaller corner and a 20px glyph, which is the kind of difference nobody can
 * name but everybody feels when they move between the two.
 */
export function Sidebar() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const current = entryFor(pathname);

  return (
    <aside className="flex h-full w-sidebar shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar">
      {/* The rail's own palette, the same one the product uses, rather than a
          card that happens to sit on the left. */}
      <div className="flex items-center gap-2.5 px-3 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary">
          <ShieldCheck className="h-4 w-4 text-sidebar-primary-foreground" />
        </div>
        <div className="min-w-0 leading-tight">
          <span className="block truncate text-sm font-semibold text-sidebar-foreground">
            Routly
          </span>
          <span className="block truncate text-[11px] text-muted-foreground">Admin console</span>
        </div>
      </div>

      <nav className="flex-1 overflow-x-hidden overflow-y-auto px-2 pb-2 scrollbar-thin">
        <ul>
          {NAVIGATION.map((entry) => {
            const isActive = entry.href === current?.href;
            return (
              <li key={entry.href}>
                <Link
                  to={entry.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(NAV_ROW, 'gap-2.5', navRowState(isActive))}
                >
                  <entry.icon className={cn(NAV_GLYPH, isActive && 'scale-110')} />
                  {/* The accent marker sits in the rail's gutter, outside the row. */}
                  {isActive ? (
                    <span className="absolute top-1.5 bottom-1.5 -left-2 w-0.5 rounded-full bg-sidebar-primary" />
                  ) : null}
                  <span className="flex-1 truncate">{entry.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <SidebarFooter />
    </aside>
  );
}
